import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaCloudUploadAlt } from "react-icons/fa";
import MainLayout from "../../components/Layouts/Main/MainLayout.index";
import {
  getDaikaiEventList,
  saveDaikaiEvent,
  deleteDaikaiEvent,
  uploadDaikaiImage,
} from "../../api/project";
import {
  MainLayout2,
  MainBody,
  Title,
  StyledCard,
  CardHeader,
  CardBody,
  TableWrapper,
  StyledTable,
  SmallButton,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  FormGrid,
  FieldBlock,
  FieldLabel,
  FormControl,
  TextArea,
  FieldHint,
  ToolbarRow,
  ModalButtons,
  PreviewImage,
  TextMuted,
} from "./DaikaiManage.style";

const DEFAULT_ITEM_JSON = JSON.stringify(
  [
    {
      name: "wantName",
      label: "大会の際の名前(＊必須)：",
      isShow: "1",
      mustFlg: "1",
      hintText: "名前",
    },
    {
      name: "email",
      label: "メールアドレス(＊必須)：",
      isShow: "1",
      mustFlg: "1",
      hintText: "",
    },
    {
      name: "tel",
      label: "電話番号(＊必須)：",
      isShow: "1",
      mustFlg: "1",
      hintText: "",
    },
    {
      name: "sameTablePro",
      label: "同卓希望プロ：\n※ご希望に添えない場合がございます",
      isShow: "1",
      mustFlg: "0",
      hintText: "村上淳プロ／坂井秀隆プロ／安藤りなプロ",
    },
    {
      name: "daikaiMemo",
      label: "メモ(任意)：",
      isShow: "1",
      mustFlg: "0",
      hintText: "",
      maxLines: 3,
      maxLength: 100,
    },
  ],
  null,
  2,
);

const EMPTY_FORM = {
  id: "",
  date_time: "",
  day_week: "",
  daikai_title1: "",
  daikai_title2: "",
  daikai_content: "",
  input_item_json: "",
  image_url: "",
  start_time: "",
  end_time: "",
  location: "",
  gamen_footer: "",
  show_order: "",
  close_date: "",
  can_not_edit_date: "",
  max_number_people: "",
};

const formatJson = (text) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return "";
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return text;
  }
};

const isValidJson = (text) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return true;
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
};

const todayPrefix = () => {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(
    d.getDate(),
  ).padStart(2, "0")}`;
};

const MAX_IMAGE_WIDTH = 1600;
const MAX_IMAGE_BYTES = 900 * 1024;

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, MAX_IMAGE_WIDTH / img.width);
        const width = Math.round(img.width * scale);
        const height = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        const makeBlob = (q) =>
          new Promise((res, rej) =>
            canvas.toBlob(
              (b) => (b ? res(b) : rej(new Error("compress failed"))),
              "image/jpeg",
              q,
            ),
          );
        (async () => {
          let quality = 0.85;
          let blob = await makeBlob(quality);
          while (blob.size > MAX_IMAGE_BYTES && quality > 0.4) {
            quality -= 0.1;
            blob = await makeBlob(quality);
          }
          resolve(
            new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", {
              type: "image/jpeg",
            }),
          );
        })().catch(reject);
      };
      img.onerror = () => reject(new Error("invalid image"));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error("read failed"));
    reader.readAsDataURL(file);
  });

export default function DaikaiManage() {
  const navigate = useNavigate();
  const [eventList, setEventList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [saving, setSaving] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const fileInputRef = useRef(null);

  useEffect(() => {
    const userId = sessionStorage.getItem("userId");
    if (!userId) {
      navigate("/");
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const res = await getDaikaiEventList();
      setEventList(res.data || []);
    } catch (error) {
      console.error("Failed to load daikai events:", error);
      toast.error("大会一覧の取得に失敗しました");
    } finally {
      setIsLoading(false);
    }
  };

  const openNew = () => {
    setEditingId("");
    setForm({ ...EMPTY_FORM, input_item_json: DEFAULT_ITEM_JSON });
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(String(item.id || ""));
    setForm({
      id: String(item.id || ""),
      date_time: item.date_time || "",
      day_week: item.day_week || "",
      daikai_title1: item.daikai_title1 || "",
      daikai_title2: item.daikai_title2 || "",
      daikai_content: item.daikai_content || "",
      input_item_json: formatJson(item.input_item_json),
      image_url: item.image_url || "",
      start_time: item.start_time || "",
      end_time: item.end_time || "",
      location: item.location || "",
      gamen_footer: item.gamen_footer || "",
      show_order: item.show_order || "",
      close_date: item.close_date || "",
      can_not_edit_date: item.can_not_edit_date || "",
      max_number_people: item.max_number_people || "",
    });
    setModalOpen(true);
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleImageUpload = async (e) => {
    const raw = e.target.files[0];
    if (!raw) return;

    if (!raw.type.startsWith("image/")) {
      toast.error("画像ファイルを選択してください");
      return;
    }

    setIsUploading(true);

    try {
      let file = raw;
      try {
        file = await compressImage(raw);
      } catch (err) {
        console.warn("圧縮に失敗したため元ファイルを使用します:", err);
      }

      const formData = new FormData();
      formData.append("image", file);
      formData.append("year", form.daikai_title1.trim());

      const response = await uploadDaikaiImage(formData);

      const imageUrl = response.data || response.url;
      if (!imageUrl) {
        toast.error("画像URLを取得できませんでした");
        return;
      }

      handleChange("image_url", imageUrl);
      toast.success(`画像「${file.name}」をアップロードしました`);
      setShowUploadModal(false);
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("アップロードに失敗しました: " + (error.message || ""));
    } finally {
      setIsUploading(false);
      e.target.value = "";
    }
  };

  const handleFormatJson = () => {
    setForm((prev) => ({ ...prev, input_item_json: formatJson(prev.input_item_json) }));
  };

  const handleInsertTemplate = () => {
    setForm((prev) => ({ ...prev, input_item_json: DEFAULT_ITEM_JSON }));
  };

  const handleSave = async () => {
    if (!form.daikai_title1.trim() || !form.daikai_title2.trim()) {
      toast.warning("大会タイトル1・大会タイトル2は必須です");
      return;
    }
    if (!form.date_time.trim()) {
      toast.warning("開催日時は必須です");
      return;
    }
    if (!/^\d{8}$/.test(form.close_date)) {
      toast.warning("締切日は8桁(yyyyMMdd)で入力してください");
      return;
    }
    if (!/^\d{8}$/.test(form.can_not_edit_date)) {
      toast.warning("キャンセル不可日は8桁(yyyyMMdd)で入力してください");
      return;
    }
    if (!isValidJson(form.input_item_json)) {
      toast.error("申込項目定義JSONの形式が正しくありません");
      return;
    }

    setSaving(true);
    try {
      await saveDaikaiEvent(form);
      toast.success(editingId ? "大会情報を更新しました" : "大会を新規作成しました");
      setModalOpen(false);
      await loadData();
    } catch (error) {
      console.error("Failed to save daikai event:", error);
      toast.error("保存に失敗しました: " + (error.message || "サーバーエラー"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const title = `${item.daikai_title1} ${item.daikai_title2}`;
    if (
      !window.confirm(
        `大会「${title}」を削除します。\n申込情報もアプリ上から非表示になります。よろしいですか？`,
      )
    ) {
      return;
    }
    try {
      await deleteDaikaiEvent(String(item.id));
      toast.success("大会を削除しました");
      await loadData();
    } catch (error) {
      console.error("Failed to delete daikai event:", error);
      toast.error("削除に失敗しました: " + (error.message || "サーバーエラー"));
    }
  };

  const selectedIsNew = !editingId;

  return (
    <MainLayout>
      <MainLayout2>
        <MainBody>
          <Title>大会管理</Title>

          <ToolbarRow>
            <SmallButton variant="success" onClick={openNew}>
              + 新規作成
            </SmallButton>
            <SmallButton variant="dark" onClick={loadData}>
              リフレッシュ
            </SmallButton>
            <TextMuted>
              登録すると雀活マスターアプリの「大会」画面に反映されます。
            </TextMuted>
          </ToolbarRow>

          <StyledCard>
            <CardHeader>
              <span>大会一覧 ({eventList.length}件)</span>
              <TextMuted style={{ color: "#fff", fontSize: "12px" }}>
                申込数は現在の登録人数
              </TextMuted>
            </CardHeader>
            <CardBody>
              <TableWrapper>
                <StyledTable>
                  <thead>
                    <tr>
                      <th style={{ width: "60px" }}>順</th>
                      <th>開催日</th>
                      <th>曜日</th>
                      <th>タイトル1</th>
                      <th>タイトル2</th>
                      <th>開始</th>
                      <th>終了</th>
                      <th>場所</th>
                      <th>締切日</th>
                      <th>最大人数</th>
                      <th style={{ width: "70px" }}>申込数</th>
                      <th style={{ width: "120px" }}>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <tr>
                        <td colSpan={12} style={{ textAlign: "center" }}>
                          読み込み中...
                        </td>
                      </tr>
                    ) : eventList.length === 0 ? (
                      <tr>
                        <td colSpan={12} style={{ textAlign: "center" }}>
                          大会が登録されていません。「新規作成」から登録してください。
                        </td>
                      </tr>
                    ) : (
                      eventList.map((item, index) => (
                        <tr key={item.id}>
                          <td>{item.show_order || "-"}</td>
                          <td>{item.date_time || "-"}</td>
                          <td>{item.day_week || "-"}</td>
                          <td>{item.daikai_title1}</td>
                          <td>{item.daikai_title2}</td>
                          <td>{item.start_time || "-"}</td>
                          <td>{item.end_time || "-"}</td>
                          <td>{item.location || "-"}</td>
                          <td>{item.close_date || "-"}</td>
                          <td>{item.max_number_people ?? "-"}</td>
                          <td>{item.participant_cnt ?? 0}</td>
                          <td>
                            <div style={{ display: "flex", gap: "5px" }}>
                              <SmallButton onClick={() => openEdit(item)}>
                                編集
                              </SmallButton>
                              <SmallButton
                                variant="danger"
                                onClick={() => handleDelete(item)}
                              >
                                削除
                              </SmallButton>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </StyledTable>
              </TableWrapper>
            </CardBody>
          </StyledCard>
        </MainBody>
      </MainLayout2>

      {modalOpen && (
        <ModalOverlay onClick={() => setModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <span>{selectedIsNew ? "大会 新規作成" : "大会 編集"}</span>
              <SmallButton variant="secondary" onClick={() => setModalOpen(false)}>
                × 閉じる
              </SmallButton>
            </ModalHeader>

            <FormGrid>
              <FieldBlock>
                <FieldLabel>開催日時</FieldLabel>
                <FormControl
                  value={form.date_time}
                  onChange={(e) => handleChange("date_time", e.target.value)}
                  placeholder="例：10/25(日)"
                />
                <FieldHint>アプリでは「開催日時 場所」の形で表示されます</FieldHint>
              </FieldBlock>

              <FieldBlock>
                <FieldLabel>曜日</FieldLabel>
                <FormControl
                  value={form.day_week}
                  onChange={(e) => handleChange("day_week", e.target.value)}
                  placeholder="例：日"
                />
              </FieldBlock>

              <FieldBlock>
                <FieldLabel>大会タイトル1(回)</FieldLabel>
                <FormControl
                  value={form.daikai_title1}
                  onChange={(e) => handleChange("daikai_title1", e.target.value)}
                  placeholder="例：2026"
                />
              </FieldBlock>

              <FieldBlock>
                <FieldLabel>大会タイトル2</FieldLabel>
                <FormControl
                  value={form.daikai_title2}
                  onChange={(e) => handleChange("daikai_title2", e.target.value)}
                  placeholder="例：ひよこ党"
                />
              </FieldBlock>

              <FieldBlock>
                <FieldLabel>大会内容</FieldLabel>
                <TextArea
                  value={form.daikai_content}
                  onChange={(e) => handleChange("daikai_content", e.target.value)}
                  placeholder="大会の説明を入力"
                  rows={3}
                />
              </FieldBlock>

              <FieldBlock>
                <FieldLabel>場所</FieldLabel>
                <FormControl
                  value={form.location}
                  onChange={(e) => handleChange("location", e.target.value)}
                  placeholder="例：浅草橋"
                />
              </FieldBlock>

              <FieldBlock>
                <FieldLabel>開始時間</FieldLabel>
                <FormControl
                  value={form.start_time}
                  onChange={(e) => handleChange("start_time", e.target.value)}
                  placeholder="例：10:00"
                />
              </FieldBlock>

              <FieldBlock>
                <FieldLabel>終了時間</FieldLabel>
                <FormControl
                  value={form.end_time}
                  onChange={(e) => handleChange("end_time", e.target.value)}
                  placeholder="例：18:00"
                />
              </FieldBlock>

              <FieldBlock>
                <FieldLabel>表示順</FieldLabel>
                <FormControl
                  type="number"
                  value={form.show_order}
                  onChange={(e) => handleChange("show_order", e.target.value)}
                  placeholder="例：4"
                />
                <FieldHint>小さいほどアプリ上の上に表示されます</FieldHint>
              </FieldBlock>

              <FieldBlock>
                <FieldLabel>最大申込人数</FieldLabel>
                <FormControl
                  type="number"
                  value={form.max_number_people}
                  onChange={(e) =>
                    handleChange("max_number_people", e.target.value)
                  }
                  placeholder="例：28"
                />
              </FieldBlock>

              <FieldBlock>
                <FieldLabel>締切日(yyyyMMdd)【必須】</FieldLabel>
                <FormControl
                  value={form.close_date}
                  onChange={(e) => handleChange("close_date", e.target.value)}
                  placeholder={`例：${todayPrefix()}`}
                />
                <FieldHint>
                  必須。この日付を過ぎると申込不可になります(アプリ判定ロジック)
                </FieldHint>
              </FieldBlock>

              <FieldBlock>
                <FieldLabel>キャンセル不可日(yyyyMMdd)【必須】</FieldLabel>
                <FormControl
                  value={form.can_not_edit_date}
                  onChange={(e) =>
                    handleChange("can_not_edit_date", e.target.value)
                  }
                  placeholder={`例：${todayPrefix()}`}
                />
                <FieldHint>
                  必須。申込済ユーザーのキャンセル不可日(アプリ判定ロジック)
                </FieldHint>
              </FieldBlock>

              <FieldBlock style={{ gridColumn: "1 / -1" }}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    alignItems: "center",
                    marginBottom: "4px",
                  }}
                >
                  <FieldLabel style={{ marginBottom: 0 }}>画像URL</FieldLabel>
                  <SmallButton
                    variant="success"
                    onClick={() => setShowUploadModal(true)}
                    disabled={isUploading}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "5px",
                    }}
                  >
                    <FaCloudUploadAlt /> 画像をアップロード
                  </SmallButton>
                </div>
                <FormControl
                  value={form.image_url}
                  onChange={(e) => handleChange("image_url", e.target.value)}
                  placeholder="例：https://xxx/images-daikai/xxx.jpg"
                />
                {form.image_url && (
                  <PreviewImage>
                    <img
                      src={form.image_url}
                      alt="preview"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </PreviewImage>
                )}
              </FieldBlock>

              <FieldBlock style={{ gridColumn: "1 / -1" }}>
                <FieldLabel>画面フッター</FieldLabel>
                <TextArea
                  value={form.gamen_footer}
                  onChange={(e) => handleChange("gamen_footer", e.target.value)}
                  placeholder="アプリの大会詳細画面下部に表示されるテキスト"
                  rows={2}
                />
              </FieldBlock>

              <FieldBlock style={{ gridColumn: "1 / -1" }}>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    alignItems: "center",
                    marginBottom: "4px",
                  }}
                >
                  <FieldLabel style={{ marginBottom: 0 }}>
                    申込項目定義JSON
                  </FieldLabel>
                  <SmallButton
                    variant="dark"
                    onClick={handleFormatJson}
                    style={{ padding: "3px 8px", fontSize: "11px" }}
                  >
                    整形
                  </SmallButton>
                  <SmallButton
                    variant="dark"
                    onClick={handleInsertTemplate}
                    style={{ padding: "3px 8px", fontSize: "11px" }}
                  >
                    雛形を挿入
                  </SmallButton>
                </div>
                <TextArea
                  value={form.input_item_json}
                  onChange={(e) => handleChange("input_item_json", e.target.value)}
                  rows={10}
                  spellCheck={false}
                />
                <FieldHint>
                  申込画面に表示する項目をJSON配列で定義します(name / label /
                  isShow / mustFlg / hintText / maxLines / maxLength が利用可能)
                </FieldHint>
              </FieldBlock>
            </FormGrid>

            <ModalButtons>
              <SmallButton variant="secondary" onClick={() => setModalOpen(false)}>
                キャンセル
              </SmallButton>
              <SmallButton
                variant="success"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "保存中…" : "保存"}
              </SmallButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}

      {showUploadModal && (
        <ModalOverlay onClick={() => setShowUploadModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <span>画像アップロード</span>
              <SmallButton
                variant="secondary"
                onClick={() => setShowUploadModal(false)}
                disabled={isUploading}
              >
                × 閉じる
              </SmallButton>
            </ModalHeader>
            <p
              style={{
                color: "#888787",
                marginBottom: "15px",
                fontSize: "14px",
              }}
            >
              大会の画像をアップロードします。アップロードした画像URLが「画像URL」欄に自動入力されます。
            </p>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              ref={fileInputRef}
              style={{
                padding: "8px",
                border: "1px solid #888787",
                borderRadius: "4px",
                width: "100%",
              }}
            />
            {isUploading && (
              <p style={{ color: "#0095FF", marginTop: "10px" }}>
                アップロード中...
              </p>
            )}
            <ModalButtons>
              <SmallButton
                variant="secondary"
                onClick={() => setShowUploadModal(false)}
                disabled={isUploading}
              >
                キャンセル
              </SmallButton>
            </ModalButtons>
          </ModalContent>
        </ModalOverlay>
      )}
    </MainLayout>
  );
}